import React, { useState } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';

import { useTranslation } from './i18n';
import ConnectedEntityPickerModal, {
  ConnectedEntityPickerModalProps,
} from './modal/ConnectedEntityPickerModal';
import type {
  Entity,
  EntityWithId,
  RelationshipDataWithState,
  UpdateEntitiesInCache,
  AddRelationships,
} from './types/types';

type ReferencePickerModalContainerProps = Pick<
  ConnectedEntityPickerModalProps,
  | 'plugin'
  | 'open'
  | 'closeModal'
  | 'container'
  | 'createFormProps'
  | 'dataViewProps'
  | 'addEntitiesToCache'
> & {
  sourceEntityId: string;
  sourceEntityType: string;
  sourceEntityDomain: string;
  onAddRelationships: AddRelationships;
  updateEntitiesInCache?: UpdateEntitiesInCache;
  relationshipsData: RelationshipDataWithState;
};

const ReferencePickerModalContainer = ({
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
  plugin,
  open,
  closeModal,
  onAddRelationships,
  relationshipsData,
  addEntitiesToCache,
  updateEntitiesInCache,
  container,
  createFormProps,
  dataViewProps,
}: ReferencePickerModalContainerProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const projectId = useCurrentProjectId();
  const [isSaving, setIsSaving] = useState(false);

  const handleAddRelationships = (
    sourceEntity: Entity,
    entitiesToLink: Entity[],
  ) => {
    setIsSaving(true);

    const handleSave = () =>
      onAddRelationships(sourceEntity, entitiesToLink)
        .then(() => {
          closeModal?.();

          if (plugin?.onAddRelationships) {
            plugin.onAddRelationships(sourceEntity, entitiesToLink, projectId);
          }
          setIsSaving(false);
        })
        .catch((error) => {
          console.error(
            'ReferencePicker - ReferencePickerModalContainer: Error adding relationships',
            error,
          );
          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: t('ERROR_ADDING_RELATIONSHIPS_MESSAGE', {
              count: entitiesToLink.length,
            }),
          });
          setIsSaving(false);
        });
    handleSave();
  };

  const onSave = (selectedEntities: EntityWithId[]) => {
    const sourceEntity = {
      id: sourceEntityId,
      type: sourceEntityType,
      domain: sourceEntityDomain,
    };
    if (!plugin) return;
    const entitiesToLink = selectedEntities.map((e) => ({
      id: e.id,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    }));
    handleAddRelationships(sourceEntity, entitiesToLink);
  };

  const onCreateSuccess = (
    entityOrEntities: EntityWithId | EntityWithId[],
    params?: { selectedEntities: EntityWithId[] },
  ) => {
    const selectedEntities = params?.selectedEntities || [];
    const sourceEntity = {
      id: sourceEntityId,
      type: sourceEntityType,
      domain: sourceEntityDomain,
    };

    const entities = Array.isArray(entityOrEntities)
      ? entityOrEntities
      : [entityOrEntities];
    const allEntities = [...entities, ...selectedEntities];
    if (!plugin) return;
    const entitiesToLink = allEntities.map((e) => ({
      id: e.id,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    }));

    addEntitiesToCache?.({
      entities,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    });

    if (!plugin?.keepEntityPickerModalOpenAfterCreate) {
      handleAddRelationships(sourceEntity, entitiesToLink);
    }
  };

  const onUpdate = (entity: EntityWithId) => {
    if (!plugin) return;
    updateEntitiesInCache?.({
      entities: [entity],
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    });
  };

  const alreadyLinkedIds = (relationshipsData?.data || []).map(
    (r) => r.entityId,
  );

  const disabledIds = [...alreadyLinkedIds, sourceEntityId];

  return (
    <ConnectedEntityPickerModal
      open={open}
      sourceEntityType={sourceEntityType}
      plugin={plugin}
      closeModal={closeModal}
      container={container}
      markAsSelectedIds={alreadyLinkedIds}
      disabledIds={disabledIds}
      onSave={onSave}
      onCreateSuccess={onCreateSuccess}
      onUpdate={onUpdate}
      isSaving={isSaving}
      loading={relationshipsData.loading}
      error={relationshipsData.error}
      retry={relationshipsData.retry}
      allowCreate={true}
      multiple={true}
      createFormProps={createFormProps}
      dataViewProps={dataViewProps}
      addEntitiesToCache={addEntitiesToCache}
    />
  );
};

export default ReferencePickerModalContainer;
